import * as Sentry from '@sentry/vue'
import { debounce, throttle, sleep } from '@zoomcatalog/shared'
import compressor from 'pako'
import { useNotification } from '@/composable/useNotification'
import { rootStore } from '@/store/rootStore'
import { queueMutator } from './queueModifiers'
import {
	ADD_PAGE,
	AutoSaveOptions,
	BaseCommand,
	CLONE_PAGE,
	Command,
	DELETE_PAGE,
	isMultiEventsCommand,
	KeepAliveCommand,
	SocketMessage,
	UPDATE_PAGE,
	UPDATE_PAGES_INDEXES,
	WebSocketPayload,
} from './types'

export class AutoSave {
	#socketUrl: string
	#socket!: WebSocket
	#loggerEnabled: boolean

	#sendingMessage = false
	#canSendMessages = false
	isSending = false
	isServerLocked = false
	#canEnqueueMessages = false
	hasServerConfirmation = false
	#messageId = 0
	#checkingForConnection = false

	#lowestSizeToCompress = 32_768
	#retryUntilMs = 500
	#debounceQueueMs = 2_000
	autoSaveInterval: any = null
	lastExpectedCommand: Command | null = null
	lastCommandSent: Command | null = null
	outgoingActions: Array<string> = []
	commandsHistory: Array<Command> = []
	socketErrors = ['locked', 'not found', 'Invalid action', 'Duplicate']
	paginationCommands: Command['action'][] = [
		ADD_PAGE,
		DELETE_PAGE,
		CLONE_PAGE,
		UPDATE_PAGE,
		UPDATE_PAGES_INDEXES,
	]
	ignoredWSCodes = [1000, 1001]

	constructor(options: AutoSaveOptions) {
		this.#loggerEnabled = options.enableLogger ?? true

		this.#socketUrl = options.socketUrl
		this.makeSocketConnection()
	}

	getBaseCommand(): BaseCommand {
		this.#messageId += 1
		const userToken = localStorage.getItem('userToken')

		return {
			endpoint: 'send_changes',
			resource: 'flyer',
			timestamp: Date.now(),
			id: this.#messageId,
			token: userToken || '',
		}
	}

	getSocket() {
		return this.#socket
	}

	hasConnection() {
		return this.#socket.readyState === this.#socket.OPEN && navigator.onLine
	}

	getSocketState() {
		const readyStateMap: Record<number, string> = {
			[this.#socket.CONNECTING]: 'CONNECTING',
			[this.#socket.OPEN]: 'OPEN',
			[this.#socket.CLOSING]: 'CLOSING',
			[this.#socket.CLOSED]: 'CLOSED',
		}
		return readyStateMap[this.#socket.readyState] || 'UNKNOWN'
	}

	setSendingMessage(value: boolean) {
		this.#sendingMessage = value
	}

	setCanSendMessages(value: boolean) {
		this.#canSendMessages = value
	}

	setCanEnqueueMessages(value: boolean) {
		this.#canEnqueueMessages = value
	}

	setIsServerLocked(value: boolean) {
		this.isServerLocked = value
	}

	makeSocketConnection() {
		if (this.#socket && this.#socket.readyState !== this.#socket.CLOSED) {
			return
		}

		this.makeNewConnection()
	}

	makeNewConnection() {
		if (this.#socket && this.#socket.readyState === this.#socket.CONNECTING)
			return
		if (this.#loggerEnabled) {
			// eslint-disable-next-line no-console
			console.info('Websocket: Making new connection')
		}
		this.#socket?.close()
		this.#socket = new WebSocket(this.#socketUrl)
		this.setupEvents()
		this.hasServerConfirmation = false
	}

	isSocketSuccess(message: SocketMessage) {
		return (
			(Array.isArray(message) && message.every((msg) => msg.code === 200)) ||
			(typeof message === 'object' &&
				(message as { code: number; message: string }).code === 200)
		)
	}

	setupEvents() {
		if (!this.autoSaveInterval) {
			this.autoSaveInterval = setInterval(() => {
				// Copy tempQueue to queue and clear tempQueue
				if (
					rootStore.state.autoSave.queue.length == 0 &&
					rootStore.state.autoSave.tempQueue.length > 0
				) {
					rootStore.state.autoSave.queue = rootStore.state.autoSave.tempQueue
					rootStore.state.autoSave.tempQueue = []
				}
				this.sendEvents(true)
			}, 1000)
		}

		this.#socket.onopen = () => {
			if (this.#loggerEnabled) {
				// eslint-disable-next-line no-console
				console.info('Websocket: socket connected')
			}
		}

		this.#socket.onclose = ($event) => {
			if (this.#loggerEnabled) {
				// eslint-disable-next-line no-console
				console.info('Websocket: socket disconnected')
			}

			if (!this.ignoredWSCodes.includes($event.code) && !navigator.onLine) {
				Sentry.captureEvent({
					message: `Websocket: was closed because there's no internet connection (code: ${$event.code})`,
					level: Sentry.Severity.Warning,
				})
			}
		}

		this.#socket.onmessage = (event: { data: string }) => {
			const { data } = event
			const message = JSON.parse(data) as SocketMessage
			if (this.#loggerEnabled) {
				if (Array.isArray(message)) {
					// eslint-disable-next-line no-console
					console.log('Websocket: responses:')
					// eslint-disable-next-line no-console
					message.forEach((msg) => console.log(msg))
				} else {
					// eslint-disable-next-line no-console
					console.log('Websocket: response:', message)
				}
			}

			const lastAction = this.outgoingActions.shift()
			if (lastAction === 'keep-alive') return (this.hasServerConfirmation = true)
			if (
				((this.lastExpectedCommand?.action === this.lastCommandSent?.action &&
					!this.isSending) ||
					(!this.lastExpectedCommand && !this.isSending)) &&
				rootStore.state.autoSave.tempQueue.length === 0 &&
				rootStore.state.autoSave.queue.length === 0
			) {
				rootStore.commit({ type: 'autoSave/setIsSending', payload: false })
			}

			if (!this.isSocketSuccess(message)) {
				this.setIsServerLocked(true)
				this.setCanSendMessages(false)

				// socket returned an error, the command will get added to the queue
				// do not send error to sentry if the error is a controlled error

				const controlledMessages = ['The resource that is being accessed is locked']
				const isControlledError = controlledMessages.some((controlledMessage) => {
					if (typeof message === 'string') {
						return message.includes(controlledMessage)
					} else if (Array.isArray(message)) {
						return message.every((msg) => {
							if (msg.message) {
								return msg.message.includes(controlledMessage)
							}
							return false
						})
					} else if (typeof message === 'object') {
						return (
							typeof message.message === 'string' &&
							message.message.includes(controlledMessage)
						)
					}
					return false
				})

				if (this.#loggerEnabled) {
					if (isControlledError) {
						// eslint-disable-next-line no-console
						console.warn('Websocket: socket controlled error (message):', message)
					} else {
						// eslint-disable-next-line no-console
						console.error('Websocket: socket error (message):', message)
					}
				}

				if (this.lastCommandSent) {
					if (typeof message === 'object') {
						for (
							let i = 0;
							i < (message as Array<{ code: number; message: string }>).length;
							i++
						) {
							if (
								(message as Array<{ code: number; message: string }>)[i].code === 428
							) {
								this.#messageId += 1
								this.lastCommandSent.id = this.#messageId
							}
						}
					}
					rootStore.state.autoSave.tempQueue.push(this.lastCommandSent)
				}

				if (!isControlledError) {
					Sentry.captureEvent({
						message: `Websocket: ${data.toString()}`,
						level: Sentry.Severity.Error,
						extra: {
							commandsHistory: this.commandsHistory,
							lastCommandSent: this.lastCommandSent,
							lastExpectedCommand: this.lastExpectedCommand,
							canSendMessages: this.#canSendMessages,
							isSending: this.isSending,
							isServerLocked: this.isServerLocked,
							canEnqueueMessages: this.#canEnqueueMessages,
							hasServerConfirmation: this.hasServerConfirmation,
							messageId: this.#messageId,
							lastReceivedMessage: message,
						},
					})
				}
			} else {
				this.setIsServerLocked(false)
				this.setCanSendMessages(true)
			}
			this.setSendingMessage(false)
		}

		this.#socket.onerror = (event) => {
			if (this.#loggerEnabled) {
				// eslint-disable-next-line no-console
				console.error('Websocket: socket error (event):', event)
			}

			if (this.lastCommandSent) {
				rootStore.state.autoSave.tempQueue.push(this.lastCommandSent)
			}

			Sentry.captureEvent({
				message: `Websocket: There was an error.`,
				level: Sentry.Severity.Error,
				extra: {
					commandsHistory: this.commandsHistory,
					lastCommandSent: this.lastCommandSent,
					lastExpectedCommand: this.lastExpectedCommand,
					canSendMessages: this.#canSendMessages,
					isSending: this.isSending,
					isServerLocked: this.isServerLocked,
					canEnqueueMessages: this.#canEnqueueMessages,
					hasServerConfirmation: this.hasServerConfirmation,
					messageId: this.#messageId,
				},
			})
			this.showNotification()
			this.setSendingMessage(false)
		}
	}

	showNotification = throttle(() => {
		const { enqueueNotification } = useNotification()
		enqueueNotification({
			message:
				"We were not able to save this design. We'll keep retrying. Refreshing might lose your work.",
			color: 'danger',
		})
	}, 10000)

	debounceQueue = debounce(async (shouldMutateQueue: boolean) => {
		this.sendEvents(shouldMutateQueue)
	}, this.#debounceQueueMs / 2)

	enqueue<T extends Command>(
		command: Omit<T, 'resource' | 'endpoint' | 'id' | 'token' | 'timestamp'>,
		options = { shouldMutate: true }
	) {
		if (!this.#canEnqueueMessages) return
		const newCommand = {
			...this.getBaseCommand(),
			...command,
		} as unknown as Command

		rootStore.commit({
			type: 'autoSave/enqueueCommand',
			payload: newCommand,
		})
		this.commandsHistory.push(newCommand)
		// rootStore.commit({ type: 'autoSave/setIsSending', payload: true })
		this.debounceQueue(options.shouldMutate)
	}

	async sendEvents(shouldMutateQueue: boolean) {
		// Evaluate if there is no other process running
		if (rootStore.state.autoSave.queue.length == 0 || this.isSending === true) {
			return
		}
		if (this.#loggerEnabled) {
			// eslint-disable-next-line no-console
			console.log('Websocket: Total queue', rootStore.state.autoSave.queue.length)
		}

		this.isSending = true
		await this.retryUntil(() => this.#canSendMessages, 100, 25)

		const queueCommands = rootStore.state.autoSave.queue
		rootStore.state.autoSave.queue = []

		const mutatedQueue = shouldMutateQueue
			? queueMutator(queueCommands)
			: queueCommands

		this.lastExpectedCommand = mutatedQueue[mutatedQueue.length - 1]

		for (const command of mutatedQueue) {
			// Re order queue to mantain FIFO
			if (!this.#canSendMessages) {
				rootStore.state.autoSave.tempQueue.push(command)
				continue
			}

			// Delay between event
			await sleep(50)

			rootStore.commit({ type: 'autoSave/setIsSending', payload: true })
			this.lastCommandSent = command
			this.outgoingActions.push(command.action)

			if (this.#loggerEnabled) {
				const logMessage = isMultiEventsCommand(command)
					? 'sending multiple commands'
					: 'sending command'
				// eslint-disable-next-line no-console
				console.log(
					`Websocket: ${logMessage} (${command.endpoint}/${command.action})`,
					isMultiEventsCommand(command) ? command.commands : command.data
				)
			}

			this.setCanSendMessages(false)

			const compressedCommand = this.compressCommand(
				command,
				this.#lowestSizeToCompress
			)
			if (compressedCommand) {
				await this.sendCommand(compressedCommand)
			}

			// Waiting until sending message it's done
			await this.retryUntil(() => !this.#sendingMessage, 100, 25)
		}

		this.isSending = false

		setTimeout(() => {
			this.setCanSendMessages(true)
		}, 1000)

		if (
			rootStore.state.autoSave.queue.length === 0 &&
			rootStore.state.autoSave.tempQueue.length === 0
		) {
			rootStore.commit({ type: 'autoSave/setIsSending', payload: false })
		}
	}

	async retryUntil(
		condition: () => Promise<boolean> | boolean,
		retryDelay = this.#retryUntilMs,
		maxRetries?: number // Optional limiter for retries
	): Promise<boolean> {
		let retries = 0

		// Continuously poll until the condition returns true or maxRetries is reached
		while (!(await condition())) {
			if (maxRetries !== undefined && retries >= maxRetries) {
				return false // Return false if the retry limit is reached
			}

			// Wait for the specified delay before trying again
			await new Promise((resolve) => setTimeout(resolve, retryDelay))

			retries++
		}

		return true // Return true when the condition becomes true
	}

	async askServerIfHasConnection(maxRetries = 10) {
		if (this.#loggerEnabled) {
			// eslint-disable-next-line no-console
			console.log('- - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -')
			const logMessage = this.#checkingForConnection
				? 'Waiting for server connection'
				: 'Asking if server has connection'

			// eslint-disable-next-line no-console
			console.log(`Websocket: ${logMessage}`)
		}

		if (this.#checkingForConnection) return

		this.hasServerConfirmation = false
		const keepAliveCommand: KeepAliveCommand = {
			...this.getBaseCommand(),
			action: 'keep-alive',
			data: {},
		}
		await this.sendCommand(keepAliveCommand)
		this.outgoingActions.push(keepAliveCommand.action)

		await this.retryUntil(() => this.hasServerConfirmation, 500, maxRetries)
		if (this.#loggerEnabled) {
			// eslint-disable-next-line no-console
			console.log(
				'Websocket: Server confirmed connection',
				this.hasServerConfirmation
			)
		}

		if (this.hasServerConfirmation) return
		this.outgoingActions.shift()
		this.makeNewConnection()
	}

	async sendCommand(command: WebSocketPayload) {
		this.#sendingMessage = true
		try {
			while (!this.hasConnection()) {
				this.#checkingForConnection = true

				const lastAction = this.outgoingActions.find(Boolean)
				if (lastAction === 'keep-alive') break

				if (this.#loggerEnabled) {
					// eslint-disable-next-line no-console
					console.warn(
						'Websocket: Waiting for connection before sending command',
						this.getSocketState(),
						navigator.onLine
					)
				}

				await sleep(1000)

				// make new connection only if the socket is not open
				// ignoring open, connecting or closing states
				if (this.#socket.readyState === this.#socket.CLOSED) {
					this.makeNewConnection()
				}
			}
			this.#checkingForConnection = false

			this.#socket.send(JSON.stringify(command))
		} catch (error) {
			if (this.#loggerEnabled) {
				// eslint-disable-next-line no-console
				console.error('Websocket: Error sending command', error)
			}
			Sentry.captureEvent({
				message: `Websocket: Error sending command, ${error}`,
				level: Sentry.Severity.Error,
			})
			this.#sendingMessage = false
		}
	}

	compressCommand(
		command: Command,
		lowestSize: number
	): WebSocketPayload | undefined {
		try {
			if (
				JSON.stringify(command).length < lowestSize ||
				isMultiEventsCommand(command)
			)
				return command
		} catch (error) {
			if (this.#loggerEnabled) {
				// eslint-disable-next-line no-console
				console.error('Websocket: Error compressing command', error)
			}
			Sentry.captureEvent({
				message: `Websocket: Error compressing command, ${error}`,
				level: Sentry.Severity.Error,
			})

			return
		}

		const commandString = JSON.stringify(command.data)

		const unicodeList = new TextEncoder().encode(commandString)

		const gzippedList = compressor.gzip(unicodeList)

		const gzippedString = Array.from(gzippedList)
			.map((unicode) => String.fromCharCode(unicode))
			.join('')

		const b64Data = btoa(gzippedString)

		return { ...command, data: b64Data }
	}

	destructor() {
		if (this.autoSaveInterval) clearInterval(this.autoSaveInterval)
	}
}
